import React, { useEffect, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, Container, IconButton } from "@mui/material";
import { Button, Stack, Typography } from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { CSDataTableColumns } from "../../../modal/CSDataTable";
import CSDataTable from "../../../components/CSDataTable";
import {
  deleteIControl,
  getIControlDetails,
  fetchControlTypeList,
  getExistingcontrol,
  deleteIControlMultiple,
} from "./helper";
import { IControl } from "./modal";
import { exportExcel } from "./utils";
import { useUserContext } from "../../../context/auth/UserContext";
import { useRiskAssessmentContext } from "../../../context/RiskAssessment/RiskAssessmentContext";
import DialogBoxComponent from "../../../components/DialogBoxComponent";
import { ControlsForm } from "./ControlForm";
import { ControlImport } from "./ControlImport";
import { CircularProgress, ListItem } from "@mui/material";
import { useAccessRights } from "../../../utils/useAccessRights";

export default function RiskAssesmentControls() {
  const riskAssesment_CS_Access = useAccessRights("Risk Assessment - CS");

  const columns: CSDataTableColumns[] = [
    {
      accessorKey: "controlsName",
      header: "Name",
      size: "70px",
    },
    {
      accessorKey: "controlsTypeName",
      header: "Type",
      size: "70px",
    },
    {
      accessorKey: "edit",
      header: "",
      size: "30px",
      isEditable: riskAssesment_CS_Access?.isUpdate,
    },
    {
      accessorKey: "delete",
      header: "",
      size: "30px",
      isDeletable: riskAssesment_CS_Access?.isDelete,
    },
  ];

  const navigate = useNavigate();

  const [selectedRow, setSelectedRow] = React.useState<any>(null);
  const [multiSelectedRow, setMultiSelectedRow] = React.useState<IControl[]>(
    []
  );
  const [openControlsForm, setOpenControlsForm] = useState(false);
  const [openControlImport, setOpenControlImport] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dialogState, setDialogState] = useState({
    title: "",
    body: "",
    action: "",
    actionCancel: "",
    openImportDialog: false,
    openDeleteConfirmationDialog: false,
    openMultipleDeleteConfirmationDialog: false,
    openSuccessDialogBox: false,
  });

  const { userId, companyProfileID } = useUserContext();
  const { controlsList, setControlsList, setControlTypeList } =
    useRiskAssessmentContext();

  const handleSelectRow = (selectedRows: IControl[]) => {
    setMultiSelectedRow(selectedRows);
  };

  const handleFormDialogClose = () => {
    setOpenControlsForm(false);
  };

  const handleCloseDialog = () => {
    setDialogState({
      ...dialogState,
      openImportDialog: false,
      openDeleteConfirmationDialog: false,
      openMultipleDeleteConfirmationDialog: false,
      openSuccessDialogBox: false,
    });
  };

  const handleAdd = () => {
    setSelectedRow(null);
    setOpenControlsForm(true);
  };

  const handleEdit = (row?: IControl) => {
    setSelectedRow(row);
    setOpenControlsForm(true);
  };

  const displayImportControl = () => {
    setOpenControlImport(true);
  };

  const handleControlImportCancel = () => {
    setOpenControlImport(false);
  };

  const confirmDeleteAll = async () => {
    const controlIds = multiSelectedRow.map((item) => item.controlsID);
    try {
      const res = await getExistingcontrol(companyProfileID, controlIds);
      if (res) {
        setDialogState({
          title: "Info",
          body: `Few Controls are assigned to Risk Assessments. Please un-assign from Risk Assessment list and then try to delete.`,
          action: "OK",
          actionCancel: "",
          openImportDialog: false,
          openDeleteConfirmationDialog: false,
          openMultipleDeleteConfirmationDialog: false,
          openSuccessDialogBox: true,
        });
      } else {
        setDialogState({
          title: "Confirmation",
          body: `Are you sure you want to delete the selected controls`,
          action: "Delete",
          actionCancel: "Cancel",
          openImportDialog: false,
          openDeleteConfirmationDialog: false,
          openMultipleDeleteConfirmationDialog: true,
          openSuccessDialogBox: false,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDelete = async (row: IControl) => {
    setSelectedRow(row);
    setIsLoading(true);
    try {
      const res = await getExistingcontrol(companyProfileID, [row.controlsID]);
      if (res) {
        setDialogState({
          title: "Info",
          body: `<strong>${row.controlsName}</strong> cannot be removed as it is assigned to Risk Assessments. Please un-assign from Risk Assessments plan and then remove.`,
          action: "OK",
          actionCancel: "",
          openImportDialog: false,
          openDeleteConfirmationDialog: false,
          openMultipleDeleteConfirmationDialog: false,
          openSuccessDialogBox: true,
        });
      } else {
        setDialogState({
          title: "Confirmation",
          body: `Are you sure you want to delete <strong>${row.controlsName}</strong>?`,
          action: "Delete",
          actionCancel: "Cancel",
          openImportDialog: false,
          openDeleteConfirmationDialog: true,
          openMultipleDeleteConfirmationDialog: false,
          openSuccessDialogBox: false,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteControls = async () => {
    handleCloseDialog();
    setIsLoading(true);
    const res = await deleteIControl(userId, selectedRow.controlsID);
    if (res.succeeded === true) {
      setDialogState({
        title: "Success",
        body: `<strong>${selectedRow.controlsName}</strong> successfully deleted`,
        action: "OK",
        actionCancel: "",
        openImportDialog: false,
        openDeleteConfirmationDialog: false,
        openMultipleDeleteConfirmationDialog: false,
        openSuccessDialogBox: true,
      });
      getControlsList();
      setMultiSelectedRow([]);
    } else {
      setDialogState({
        title: "Error",
        body: "Unexpected Error",
        action: "OK",
        actionCancel: "",
        openImportDialog: false,
        openDeleteConfirmationDialog: false,
        openMultipleDeleteConfirmationDialog: false,
        openSuccessDialogBox: true,
      });
    }
  };
  const handleMultipleDeleteConfirm = async () => {
    handleCloseDialog();
    setIsLoading(true);
    const payload = multiSelectedRow.map((item) => item.controlsID);
    const res = await deleteIControlMultiple(userId, payload);

    if (res.succeeded) {
      setDialogState({
        title: "Success",
        body: `Controls has been deleted successfully `,
        action: "OK",
        actionCancel: "",
        openImportDialog: false,
        openDeleteConfirmationDialog: false,
        openMultipleDeleteConfirmationDialog: false,
        openSuccessDialogBox: true,
      });
      getControlsList();
    } else {
      setDialogState({
        title: "Error",
        body: "Unexpected Error",
        action: "OK",
        actionCancel: "",
        openImportDialog: false,
        openDeleteConfirmationDialog: false,
        openMultipleDeleteConfirmationDialog: false,
        openSuccessDialogBox: true,
      });
    }
  };

  const getControlsList = async () => {
    setIsLoading(true);
    try {
      const controlTypeList = await fetchControlTypeList();
      setControlTypeList(controlTypeList);
      const res = await getIControlDetails(companyProfileID);
      const formatList = res
        .map((item) => ({
          ...item,
          rowId: item.controlsID,
          controlsTypeName:
            controlTypeList.find(
              (ele: { controlsTypeID: number }) =>
                ele.controlsTypeID === item.controlsTypeId
            )?.controlsTypeName || "",
        }))
        .sort((a, b) => {
          const nameA = a.controlsName || "";
          const nameB = b.controlsName || "";
          return nameA.localeCompare(nameB);
        });
      setControlsList(formatList);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoading) getControlsList();
  }, []);

  return (
    <Fragment>
      <Container sx={{ p: 1, height: "100%" }}>
        <Card sx={{ borderRadius: "10px", overflow: "hidden", height: "100%" }}>
          <CardContent>
            <Stack
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
            >
              <Stack alignItems="center" direction="row">
                <IconButton onClick={() => navigate("/risk-assessment")}>
                  <KeyboardBackspaceIcon />
                </IconButton>
                <Typography>Controls</Typography>
              </Stack>
              <Stack alignItems="center" direction="row" spacing={2}>
                <Button
                  size="small"
                  variant="contained"
                  className="table-btn"
                  disabled={multiSelectedRow.length < 1}
                  onClick={() => confirmDeleteAll()}
                >
                  Delete
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  className="table-btn"
                  onClick={displayImportControl}
                  disabled={!riskAssesment_CS_Access?.isImport || isLoading}
                >
                  Import
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  className="table-btn"
                  onClick={handleAdd}
                  disabled={!riskAssesment_CS_Access?.isCreate || isLoading}
                >
                  Create New Controls
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  className="table-btn"
                  disabled={isLoading}
                  onClick={() => exportExcel(controlsList)}
                >
                  Download Spreadsheet
                </Button>
              </Stack>
            </Stack>
            <Container
              maxWidth="md"
              sx={{
                mt: 1,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {isLoading ? (
                <ListItem
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <CircularProgress />
                </ListItem>
              ) : (
                <CSDataTable
                  rows={controlsList}
                  columns={columns}
                  multiRowSelection={true}
                  handleEdit={handleEdit}
                  handleDelete={confirmDelete}
                  handleSelectRow={(selectedRows) =>
                    handleSelectRow(selectedRows)
                  }
                />
              )}
            </Container>
          </CardContent>
        </Card>
      </Container>
      {
        <ControlsForm
          openDialogBox={openControlsForm}
          list={controlsList}
          selectedRow={selectedRow}
          handleCancel={handleFormDialogClose}
          handleRefresh={() => getControlsList()}
        />
      }

      {
        <ControlImport
          openDialog={openControlImport}
          controlList={controlsList}
          handleCancel={handleControlImportCancel}
          refreshList={() => getControlsList()}
        />
      }
      {
        //Delete Confirm
        <DialogBoxComponent
          openDialogBox={dialogState.openDeleteConfirmationDialog}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogActionCancel={dialogState.actionCancel}
          dialogBody={dialogState.body}
          handleFunction={handleDeleteControls}
          handleCancel={handleCloseDialog}
        />
      }
      {
        //Delete Multiple Confirm
        <DialogBoxComponent
          openDialogBox={dialogState.openMultipleDeleteConfirmationDialog}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogActionCancel={dialogState.actionCancel}
          dialogBody={dialogState.body}
          handleFunction={handleMultipleDeleteConfirm}
          handleCancel={handleCloseDialog}
        />
      }
      {
        //Success
        <DialogBoxComponent
          openDialogBox={dialogState.openSuccessDialogBox}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogBody={dialogState.body}
          handleFunction={handleCloseDialog}
        />
      }
    </Fragment>
  );
}
